import dayjs from "dayjs";
import type { ActivityBucket } from "../../../queries/reports";
import { formatHours, linePoints, niceMax } from "./format";
import styles from "./dash.module.css";

const W = 640;
const H = 230;
const PAD = { l: 34, r: 12, t: 14, b: 30 };

/** Tickets opened vs solved (lines) over logged-hours bars. Pure SVG, token colours. */
export const ActivityChart = ({ data, granularity }: { data: ActivityBucket[]; granularity: "day" | "week" }) => {
  const max = niceMax(Math.max(0, ...data.flatMap((d) => [d.ticketsOpened, d.ticketsSolved])));
  const maxMin = Math.max(1, ...data.map((d) => d.totalMinutes));
  const opened = linePoints(data.map((d) => d.ticketsOpened), W, H, PAD, max);
  const solved = linePoints(data.map((d) => d.ticketsSolved), W, H, PAD, max);
  const plotH = H - PAD.t - PAD.b;
  const plotW = W - PAD.l - PAD.r;
  const barW = Math.max(4, Math.min(28, (plotW / Math.max(data.length, 1)) * 0.5));
  const ticks = [0, max / 2, max];
  const every = Math.ceil(data.length / 8);
  const label = (b: string) => dayjs(b).format("D MMM");
  const path = (pts: { x: number; y: number }[]) => pts.map((p, i) => `${i ? "L" : "M"}${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(" ");

  return (
    <div>
      <ul className={styles.legend} aria-label="Legend">
        <li className={styles.legendItem}>
          <svg width="22" height="10" aria-hidden="true"><line x1="0" y1="5" x2="22" y2="5" className={styles.lnOpened} /><circle cx="11" cy="5" r="3.5" className={styles.ptOpened} /></svg>
          Tickets opened
        </li>
        <li className={styles.legendItem}>
          <svg width="22" height="10" aria-hidden="true"><line x1="0" y1="5" x2="22" y2="5" className={styles.lnSolved} /><rect x="7.5" y="1.5" width="7" height="7" className={styles.ptSolved} /></svg>
          Tickets solved
        </li>
        <li className={styles.legendItem}>
          <span className={styles.swatch} style={{ background: "var(--osi-brand-200)" }} aria-hidden="true" />
          Logged hours (bars)
        </li>
      </ul>
      <svg viewBox={`0 0 ${W} ${H}`} className={styles.chart} role="img"
        aria-label={`Tickets opened and solved per ${granularity}, with logged hours. Full numbers are in the data table below.`}>
        {ticks.map((t) => {
          const y = PAD.t + plotH - (t / max) * plotH;
          return (
            <g key={t}>
              <line x1={PAD.l} x2={W - PAD.r} y1={y} y2={y} className={styles.grid} />
              <text x={PAD.l - 6} y={y + 4} textAnchor="end" className={styles.axis}>{Math.round(t)}</text>
            </g>
          );
        })}
        {data.map((d, i) => {
          const h = (d.totalMinutes / maxMin) * plotH * 0.5;
          return (
            <rect key={d.bucket} x={opened[i].x - barW / 2} y={PAD.t + plotH - h} width={barW} height={h} className={styles.hoursBar}>
              <title>{`${label(d.bucket)}: ${formatHours(d.totalMinutes)} logged`}</title>
            </rect>
          );
        })}
        <path d={path(opened)} className={styles.lnOpened} fill="none" />
        <path d={path(solved)} className={styles.lnSolved} fill="none" />
        {data.map((d, i) => (
          <g key={d.bucket}>
            <circle cx={opened[i].x} cy={opened[i].y} r="3.5" className={styles.ptOpened}>
              <title>{`${label(d.bucket)}: ${d.ticketsOpened} opened`}</title>
            </circle>
            <rect x={solved[i].x - 3.5} y={solved[i].y - 3.5} width="7" height="7" className={styles.ptSolved}>
              <title>{`${label(d.bucket)}: ${d.ticketsSolved} solved`}</title>
            </rect>
            {i % every === 0 && (
              <text x={opened[i].x} y={H - 8} textAnchor="middle" className={styles.axis}>{label(d.bucket)}</text>
            )}
          </g>
        ))}
      </svg>
      <details className={styles.details}>
        <summary>View data as a table</summary>
        <div className={styles.tableWrap}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th scope="col">{granularity === "week" ? "Week of" : "Day"}</th>
                <th scope="col" className={styles.num}>Opened</th>
                <th scope="col" className={styles.num}>Solved</th>
                <th scope="col" className={styles.num}>Logged</th>
              </tr>
            </thead>
            <tbody>
              {data.map((d) => (
                <tr key={d.bucket}>
                  <th scope="row" className={styles.rowHead}>{label(d.bucket)}</th>
                  <td className={`${styles.num} osi-num`}>{d.ticketsOpened}</td>
                  <td className={`${styles.num} osi-num`}>{d.ticketsSolved}</td>
                  <td className={`${styles.num} osi-num`}>{formatHours(d.totalMinutes)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </details>
    </div>
  );
};
